<!--
  - Copyright (c) 2019 brightlamp
  -->

<template>
    <div>
        <v-alert
                v-model="exportAlert"
                dismissible
                type="success">
            Export requested.
        </v-alert>
        <v-toolbar flat color="white">
            <v-toolbar-title>{{ mrnPredicate.mrn }}</v-toolbar-title>
            <v-icon large class="ml-6" @click="loadItems(true)">
                mdi-refresh
            </v-icon>
            <v-dialog v-model="dialog" scrollable max-width="1120px" :persistent="noteChanged">
                <v-card>
                    <v-card-title>
                        <span class="headline" align="center" style="color:#FFFF00;background-color:#363636;width:100%;display:block;">{{ formTitle }}</span>
                    </v-card-title>
                    <v-card-actions>
                        <v-tabs v-if="'PLR' === this.resultData.operationalMode"
                            v-model="tab"
                            align-with-title
                            background-color="white">
                            <v-tabs-slider color="blue"/>
                            <v-tab
                                v-for="item in tabItems"
                                :key="item">
                                {{ item }}
                            </v-tab>
                        </v-tabs>
                        <v-spacer v-else></v-spacer>
                        <v-btn class="pr-5" color="blue darken-1" text @click="close">Close</v-btn>
                    </v-card-actions>
                    <v-card-text id="resultDataId">
                        <v-toolbar dense class="mb-2 indigo white--text" flat>
                            <v-toolbar-title>Patient Information</v-toolbar-title>
                        </v-toolbar>
                        <v-row>
                            <v-col xs="12" sm="6">
                                <TestPatientData v-if="loaded" :resultData="resultData"/>
                            </v-col>
                        </v-row>
                        <v-toolbar dense class="mb-2 indigo white--text" flat>
                            <v-toolbar-title>Video</v-toolbar-title>
                        </v-toolbar>
                        <v-container>
                            <v-row>
                                <v-col xs="12" sm="6">
                                    <template v-if="loaded && tab != null && resultData.eyes[2 === tab ? 0 : tab].eyeVideoFileUrl && resultData.eyes[2 === tab ? 0 : tab].eyeVideoFileUrl !== 'HTTPS://'">
                                        <vue-plyr class="wrapper" :options="playerOptions">
                                            <video :src="resultData.eyes[2 === tab ? 0 : tab].eyeVideoFileUrl" />
                                        </vue-plyr>
                                        <v-container v-if="currentUser && currentUser.authorities && currentUser.authorities[0].role === 'ROLE_ADMIN'" fluid grid-list-md>
                                            <strong><a download v-bind:href="resultData.eyes[2 === tab ? 0 : tab].eyeVideoFileUrl">Download Video: Right-click here and choose Save or Download option.</a></strong>
                                        </v-container>
                                    </template>
                                </v-col>
                                <v-col v-if="loaded && tab === 2 && resultData.eyes[1].eyeVideoFileUrl">
                                    <vue-plyr class="wrapper" :options="playerOptions">
                                        <video :src="resultData.eyes[1].eyeVideoFileUrl" />
                                    </vue-plyr>
                                    <v-container v-if="currentUser && currentUser.authorities && currentUser.authorities[0].role === 'ROLE_ADMIN'" fluid grid-list-md>
                                        <strong><a download v-bind:href="resultData.eyes[1].eyeVideoFileUrl">Download Video: Right-click here and choose Save or Download option.</a></strong>
                                    </v-container>
                                </v-col>
                            </v-row>
                        </v-container>
                        <TestPupillogram v-if="loaded && showPupillogram" :eyeChoice="2 === resultData.eyes.length ? calcTab : 'Right' === resultData.eyes[0].eyeLocation ? 0 : 1" :tests="tests" :test1="resultData.eyes[2 === calcTab ? 0 : calcTab ? calcTab : 0]" :test2="2 === resultData.eyes.length ? resultData.eyes[1] : null"/>
                        <v-toolbar dense class="mb-2 indigo white--text" flat>
                            <v-toolbar-title>Results</v-toolbar-title>
                        </v-toolbar>
                        <v-row>
                            <v-col v-if="loaded && 0 === tab" xs="12" sm="6">
                                <TestResultData :tab="tab" :patientDob="resultData.dob" :resultData="resultData.eyes[0]" :resultDataOtherEye="resultData.eyes[1]"/>
                            </v-col>
                            <v-col v-if="loaded && 1 === tab" xs="12" sm="6">
                                <TestResultData :tab="tab" :patientDob="resultData.dob" :resultData="resultData.eyes[1]" :resultDataOtherEye="resultData.eyes[0]"/>
                            </v-col>
                            <template v-if="loaded && 2 === tab">
                                <v-col xs="12" sm="6">
                                    <TestResultData :tab="2" :patientDob="resultData.dob" :resultData="resultData.eyes[0]" :resultDataOtherEye="resultData.eyes[1]"/>
                                </v-col>
                                <v-col>
                                    <TestResultData :tab="2" :patientDob="resultData.dob" :resultData="resultData.eyes[1]" :resultDataOtherEye="resultData.eyes[0]"/>
                                </v-col>
                            </template>
                        </v-row>
                        <v-toolbar dense class="mb-2 indigo white--text" flat>
                            <v-row align="center">
                                <v-col>
                                    <v-toolbar-title>Concussion Information</v-toolbar-title>
                                </v-col>
                                <v-col md="2">
                                    <v-btn color="primary" @click="editConcussionData">Edit</v-btn>
                                </v-col>
                            </v-row>
                        </v-toolbar>
                        <v-row>
                            <v-col xs="12" sm="6">
                                <TestConcussionData Data v-if="loaded" :resultData="resultData"/>
                            </v-col>
                        </v-row>
                        <v-toolbar dense class="mb-2 indigo white--text" flat>
                            <v-toolbar-title>Test Information</v-toolbar-title>
                        </v-toolbar>
                        <v-row>
                            <v-col xs="12" sm="6">
                                <TestMetaData v-if="loaded" :resultData="resultData.eyes[0]"/>
                            </v-col>
                            <v-col v-if="loaded && 2 === tab">
                                <TestMetaData :resultData="resultData.eyes[1]"/>
                            </v-col>
                        </v-row>
                        <TestNotes class="mt-n6" v-if="loaded" :mrn="resultData.mrn" :resultData="resultData.eyes[0]" :resetData="resetData" @save-note="saveNote" @note-changed="noteChangedFunc"/>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </v-toolbar>
        <v-container fluid>
            <v-row no-gutters>
                <v-col>
                    <v-data-table
                        v-model="selected"
                        :headers="headers"
                        :items="tests"
                        item-key="eyes[0].visitId"
                        @click:row="rowSelected"
                        @item-selected="rowChecked"
                        :show-select="showExportCheckboxes"
                        :loading="waiting"
                        disable-pagination
                        hide-default-footer
                        class="elevation-1"
                        @update:options="loadItems()">
                        <template v-slot:header.data-table-select="{ props }">
                        </template>
                        <template v-slot:item.visitDate="{ item }">
                            <v-badge
                                :value = item.showNotViewedDot
                                color="red"
                                dot>
                                {{ item.visitDate }}
                            </v-badge>
                        </template>
                        <template v-slot:item.reflexScores="{ item }">
                            <v-chip label :style="{color:'white', backgroundColor: item.eyes[0].reflexScoreColor}">{{ item.eyes[0].reflexScoreShortEye.trim() }}</v-chip>
                            <v-chip v-if="2 === item.eyes.length" label :style="{color: 'white', backgroundColor: item.eyes[1].reflexScoreColor}">{{ item.eyes[1].reflexScoreShortEye.trim() }}</v-chip>
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <v-menu offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn color="accent" v-on="on">
                                        <span style="color: #000000;">Actions</span>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item
                                        v-for="(actionItem, index) in actionItems"
                                        :key="index"
                                        @click="testAction(index, item)">
                                        <v-list-item-icon><v-icon color="black" v-text="actionItem.icon"></v-icon></v-list-item-icon>
                                        <v-list-item-content><v-list-item-title v-text="actionItem.title"></v-list-item-title></v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>
        <TestConcussionEditDialog v-if="resultData.eyes" :dialog="editConcussionDialog" :mrn="resultData.mrn" :resultData="resultData.eyes[0]" @dialog-close="editConcussionDataHandler"/>
        <ConfirmationDialog v-if="confirmLoseChanges" :params="confirmLoseChangesParams" @confirmation-dialog-response="confirmLoseChangesHandler"/>
        <ConfirmationDialog v-if="confirmDeleteTest" :params="confirmDeleteTestParams" @confirmation-dialog-response="confirmDeleteTestHandler"/>
        <ConfirmationDialog v-if="confirmCouldNotToggleTest" :params="confirmCouldNotToggleTestParams" @confirmation-dialog-response="confirmCouldNotToggleTestHandler"/>
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations } from 'vuex'
    import VuePlyr from 'vue-plyr'
    import TestPatientData from '@/components/test-patient-data'
    import TestPupillogram from '@/components/test-pupillogram'
    import TestResultData from '@/components/test-result-data'
    import TestConcussionData from '@/components/test-concussion-data'
    import TestConcussionEditDialog from '@/components/test-concussion-edit-dialog'
    import TestMetaData from '@/components/test-meta-data'
    import TestNotes from '@/components/test-notes'
    import ConfirmationDialog from '@/components/confirmation-dialog'

    export default
    {
        name: "list-mrn-tests",
        components:
        {
            VuePlyr,
            TestPatientData,
            TestPupillogram,
            TestResultData,
            TestConcussionData,
            TestConcussionEditDialog,
            TestMetaData,
            TestNotes,
            ConfirmationDialog
        },
        data: function ()
        {
            return {
                doSelectRowAction: true,
                dialog: false,
                selected: [],
                headers:
                [
                    {
                        text: 'Actions',
                        value: 'actions',
                        sortable: false
                    },
                    {
                        text: 'Test Date',
                        value: 'visitDate',
                        width: '200px',
                        sortable: false
                    },
                    {
                        text: 'Test Mode',
                        value: 'operationalMode',
                        sortable: false
                    },
                    {
                        text: 'Eye Tested',
                        value: 'eyeLocation',
                        sortable: false
                    },
                    {
                        text: 'Reflex Score',
                        value: 'reflexScores',
                        sortable: false
                    },
                    {
                        text: 'Test Protocol Tag',
                        value: 'protoTag',
                        sortable: false
                    },
                    {
                        text: 'Test Administrator',
                        value: 'displayName',
                        sortable: false
                    }
                ],
                actionItems:
                [
                    { title: 'Export', icon: 'mdi-email' },
                    { title: 'Delete', icon: 'mdi-delete' },
                    { title: 'Toggle Baseline On/Off', icon: 'mdi-autorenew' }
                ],
                resultData: {},
                resetData: false,
                playerOptions:
                {
                    controls: ['play-large', 'play', 'progress', 'current-time', 'fullscreen']
                },
                loaded: false,
                exportAlert: false,
                noteChanged: false,
                confirmLoseChanges: false,
                confirmLoseChangesParams:
                {
                    title: 'About to Lose Changes',
                    text: 'You have an unsaved note. Lose changes?',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No'
                },
                confirmDeleteTest: false,
                confirmDeleteTestParams:
                {
                    title: 'Delete Test',
                    text: '',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No',
                    context: ''
                },
                confirmCouldNotToggleTest: false,
                confirmCouldNotToggleTestParams:
                {
                    title: 'Could Not Toggle Test',
                    text: 'A test cannot be converted to a baseline if it is missing key parameters for the particular test type. For a two eye test this is true for both eyes.',
                    positiveResponse: 'OK'
                },
                tab: null, // Which tab is selected, 0 = Right, 1 = Left, 2 = Both
                tabItems: [],
                editConcussionDialog: false
            }
        },
        props:
        {
            mrnPredicate: {},
            showExportCheckboxes: Boolean(true)
        },
        computed:
        {
            ...mapState('auth',
            {
                currentUser: state => state.currentUser,
                seeOrganizationTests: state => state.seeOrganizationTests
            }),
            ...mapState('tests',
            {
                tests: state => state.tests,
                concussionDataToMutate: state => state.concussionDataToMutate
            }),
            ...mapState('exportResult',
            {
                testSelections: state => state.testSelections
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
            formTitle()
            {
                return `Test Results for Patient ID: ${this.mrnPredicate.mrn}`
            },
            showPupillogram()
            {
                if ('PLR' === this.resultData.eyes[0].operationalMode)
                {
                    if (2 === this.tab && this.resultData.eyes[0].errCode != null && this.resultData.eyes[1].errCode != null && (0 === this.resultData.eyes[0].errCode || 0 === this.resultData.eyes[1].errCode))
                    {
                        return true
                    }
                    if (1 === this.tab && this.resultData.eyes[1].errCode != null && 0 === this.resultData.eyes[1].errCode)
                    {
                        return true
                    }
                    if (1 === this.tab && (null == this.resultData.eyes[1].errCode || this.resultData.eyes[1].errCode !== 0))
                    {
                        return false
                    }
                    return (this.resultData.eyes[0].errCode != null && 0 === this.resultData.eyes[0].errCode)
                }
                else // NPC
                {
                    return false
                }
            },
            calcTab()
            {
                if (2 === this.tab && this.resultData.eyes[0].errCode != null && this.resultData.eyes[1].errCode != null && 0 === this.resultData.eyes[0].errCode && 0 === this.resultData.eyes[1].errCode)
                {
                    return 2
                }
                if (2 === this.tab && this.resultData.eyes[0].errCode != null && this.resultData.eyes[1].errCode != null && (0 === this.resultData.eyes[0].errCode || 0 === this.resultData.eyes[1].errCode))
                {
                    return 0 === this.resultData.eyes[0].errCode ? 0 : 1
                }
                if (1 === this.tab && this.resultData.eyes[1].errCode != null && 0 === this.resultData.eyes[1].errCode)
                {
                    return 1
                }
                return 0
            }
        },
        watch:
        {
            dialog(val)
            {
                if (!val)
                {
                    document.getElementById('resultDataId').scrollTop = 0;
                    this.tabItems = [];
                    this.resetData = true
                }
                else
                {
                    this.resetData = false
                }
            },
            async mrnPredicate()
            {
                await this.loadItems()
            },
            testSelections()
            {
                this.selected = this.testSelections
            }
        },
        methods:
        {
            ...mapActions('tests', ['GET_TESTS', 'GET_TEST_VIDEO_PAIR', 'DELETE_TEST', 'SET_TEST_VIEWED_NO_WAIT', 'TOGGLE_BASELINE_SETTING']),
            ...mapMutations('tests', ['CLEAR_TESTS', 'UPDATE_TEST_PAIR_FOR_BASELINE']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            ...mapActions('exportResult', ['EXPORT_RESULT_DATA']),
            ...mapMutations('exportResult', ['ADD_TEST_SELECTION']),
            ...mapMutations('mrns', ['UPDATE_TEST_PENDING_VIEW_COUNT_LOCALLY']),
            async rowSelected(item)
            {
                if (this.doSelectRowAction)
                {
                    await this.postTestSelection(item)
                }
                this.doSelectRowAction = true
            },
            calcTabbedChoices()
            {
                if (1 === this.resultData.eyes.length)
                {
                    this.tabItems.push(this.resultData.eyeLocation)
                }
                else if (2 === this.resultData.eyes.length)
                {
                    this.tabItems.push('Right Eye');
                    this.tabItems.push('Left Eye');
                    this.tabItems.push('Both Eyes')
                }
                this.tab = 2 === this.resultData.eyes.length ? 2 : 0
            },
            async loadItems(clearTests)
            {
                try
                {
                    // If we should clear the tests so we reload the tests from the server
                    if (clearTests)
                    {
                        this.CLEAR_TESTS()
                    }
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    // uId is undefined unless a test for another user is requested
                    await this.GET_TESTS({mrn: this.mrnPredicate.mrn, uId: this.mrnPredicate.uId});
                    this.selected = this.testSelections;
                    if (this.mrnPredicate.testToken)
                    {
                        const item = this.tests.find(test => test.testToken === this.mrnPredicate.testToken);
                        await this.postTestSelection(item)
                    }
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            },
            async loadItemTestVideoPair(test)
            {
                if (test.eyes.length > 0 && !test.eyes[0].eyeVideoFileUrl?.startsWith('https://'))
                {
                    try
                    {
                        this.HIDE_SNACKBAR();
                        this.WAITING(true);
                        // uId is undefined unless a test for another user is requested
                        await this.GET_TEST_VIDEO_PAIR({mrn: test.mrn, visitId: test.eyes[0].visitId, uId: this.mrnPredicate.uId})
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message);
                    }
                    this.WAITING(false)
                }
            },
            async postTestSelection(test)
            {
                await this.loadItemTestVideoPair(test);
                this.resultData = Object.assign({}, test);
                this.calcTabbedChoices();
                this.loaded = true;
                this.dialog = true;
                // If there is a test token associated with this test and it has not been viewed yet, send an asynchronous message to update its status as viewed
                if (test.testToken && !test.beenViewed)
                {
                    test.beenViewed = true;
                    test.showNotViewedDot = false;
                    this.UPDATE_TEST_PENDING_VIEW_COUNT_LOCALLY(this.mrnPredicate.mrn);
                    this.SET_TEST_VIEWED_NO_WAIT({testToken: test.testToken, visitId: test.eyes[0].visitId, visitIdPair: test.eyes[0].visitIdPair}).catch()
                }
            },
            close()
            {
                if (this.noteChanged)
                {
                    this.confirmLoseChanges = true;
                    return
                }
                this.dialog = false
            },
            async testAction(index, item)
            {
                switch (index)
                {
                    case 0: // Export
                        await this.exportDataSingleItem(item);
                        break
                    case 1: // Delete
                        await this.deleteItem(item);
                        break
                    default: // Toggle Baseline
                        await this.toggleBaseline(item)
                }
            },
            async deleteItem(item)
            {
                this.doSelectRowAction = false;
                this.confirmDeleteTestParams.text = `Are you sure you want to delete test: ${item.visitDate}?`;
                this.confirmDeleteTestParams.context = item;
                this.confirmDeleteTest = true
            },
            async toggleBaseline(item)
            {
                // If one or more of the tests cannot be toggled
                if (
                    !item.eyes[0].canToggleBaseline ||
                    (item.eyes.length > 1 && !item.eyes[1].canToggleBaseline)
                )
                {
                    this.confirmCouldNotToggleTest = true
                    return
                }
                this.doSelectRowAction = false;
                this.HIDE_SNACKBAR();
                this.WAITING(true);
                try
                {
                    let baseline = 'Yes' !== item.eyes[0].baseline;
                    await this.TOGGLE_BASELINE_SETTING({mrn: item.mrn, visitId: item.eyes[0].visitId, data: {baseline: baseline}})
                    baseline = baseline ? 'Yes' : 'No';
                    item.eyes[0].baseline = baseline;
                    if (item.eyes.length > 1)
                    {
                        item.eyes[1].baseline = baseline
                    }
                    this.UPDATE_TEST_PAIR_FOR_BASELINE(item)
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message)
                }
                this.WAITING(false)
            },
            async confirmCouldNotToggleTestHandler()
            {
                this.confirmCouldNotToggleTest = false
            },
            async exportDataSingleItem(item)
            {
                this.doSelectRowAction = false;
                this.HIDE_SNACKBAR();
                this.WAITING(true);
                try
                {
                    await this.EXPORT_RESULT_DATA({includeOrg: this.seeOrganizationTests, tests: [{mrn: this.mrnPredicate.mrn, visitIds: [item.eyes[0].visitId]}]});
                    this.exportAlert = true;
                    setTimeout(() =>
                    {
                        this.exportAlert = false
                    }, 1500)
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message)
                }
                this.WAITING(false)
            },
            rowChecked(item)
            {
                this.ADD_TEST_SELECTION(item.item);
                this.$emit('testSelectedMsg', this.testSelections.some(item => item.mrn === this.mrnPredicate.mrn))
            },
            saveNote: function(payload)
            {
                // Find the correct test to update locally
                for (let test of this.tests)
                {
                    if (test.eyes[0].visitId === payload.visitId)
                    {
                        test.eyes[0].patientNote = payload.patientNote;
                        this.noteChanged = false;
                        break
                    }
                }
            },
            noteChangedFunc: function(needToSave)
            {
                this.noteChanged = needToSave
            },
            confirmLoseChangesHandler(payload)
            {
                this.confirmLoseChanges = false;
                // If the changes should be lost
                if (payload.response)
                {
                    this.dialog = false
                }
            },
            async confirmDeleteTestHandler(payload)
            {
                this.confirmDeleteTest = false;
                // If the test should be deleted
                if (payload.response)
                {
                    try
                    {
                        this.HIDE_SNACKBAR();
                        this.WAITING(true);
                        // uId is undefined unless a test for another user is requested
                        await this.DELETE_TEST({mrn: this.mrnPredicate.mrn, visitId: payload.context.eyes[0].visitId, uId: this.mrnPredicate.uId});

                        this.UPDATE_TEST_PENDING_VIEW_COUNT_LOCALLY(this.mrnPredicate.mrn);
                        // Refresh items if there are more tests left
                        if (this.tests.length > 0)
                        {
                            await this.loadItems(true)
                        }
                        else // Else refresh the parent view
                        {
                            this.$emit('refreshMsg')
                        }
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message)
                    }
                    this.WAITING(false)
                }
            },
            editConcussionData()
            {
                this.editConcussionDialog = true
            },
            editConcussionDataHandler(payload)
            {
                this.editConcussionDialog = false;
                // If the concussion data for the local record should be updated
                if (payload)
                {
                    // Find the correct test to update locally
                    for (let test of this.tests)
                    {
                        if (test.eyes[0].visitId === this.resultData.eyes[0].visitId)
                        {
                            test.eyes[0].concussionData = Object.assign({}, this.concussionDataToMutate);
                            test.concussionData = Object.assign({}, this.concussionDataToMutate);
                            this.resultData = Object.assign({}, test);
                            break
                        }
                    }
                }
            }
        }
    }
</script>

<style lang="scss" rel="stylesheet/scss" scoped>    
    .wrapper
    {
        width: 500px;
        height: 420px;
    }
</style>